import type {
  CompleteEvaluationSessionPayload,
  CreateEvaluationSessionPayload,
  EvaluationDetailsResult,
  EvaluationListItem,
  EvaluationSession,
  EvaluationSessionWithQuestions,
  ExportPdfEvaluationSessionResponse,
  GenerateConclusionEvaluationSessionResponse,
  GetEvaluationSessionsPayload,
} from '../../types/index.js';
import { baseApi } from '../base-api.js';

type EvaluationsResponse = { evaluations: EvaluationListItem[] };
type SessionDetailsResponse = { sessionDetails: EvaluationDetailsResult };
type ResumeResponse = { evaluationSession: EvaluationSessionWithQuestions };
type CreateSessionResponse = { evaluationSession: EvaluationSessionWithQuestions; message: string };
type CompleteSessionResponse = { evaluationSession: EvaluationSession; message: string };
type DeleteSessionResponse = { deleted: boolean; message: string };
type ArchiveSessionResponse = { archived: boolean; message: string };
type UnarchiveSessionResponse = { unarchived: boolean; message: string };
type GenerateConclusionResponse = GenerateConclusionEvaluationSessionResponse & { message: string };

const patientListTag = (patientId: string) => ({ type: 'EvaluationSession' as const, id: `PATIENT_${patientId}` });

/**
 * Mirrors `apps/server/src/modules/evaluation-session` (`evaluationSessionController`, prefix
 * `/evaluation-sessions`) exactly, post the ownership-enforcement security fix (commit
 * a6fe92af, "enforce session ownership" — project memory, 2026-09 audit). Every read/mutation
 * that targets a specific session scopes the query by `practitioner_id` server-side
 * (`findByIdAndPractitionerId`, `getEvaluationSessionForResume(id, user.id)`,
 * `deleteEvaluationSession(id, user.id)`, the `...ByEvaluationSessionIdAndPractitionerId`
 * archive/unarchive methods) — there is no "get session by id" variant here that skips that
 * check, intentionally, so this file can't reintroduce the IDOR.
 *
 * `deleteEvaluationSession`/`archiveEvaluationSession`/`unarchiveEvaluationSession` keep their
 * `deleted`/`archived`/`unarchived` boolean in the response (only `message` is stripped), mirroring
 * the server envelope. In practice a missing or unowned session ERRORS rather than returning
 * `false`: delete surfaces as a 500 (uncaught repository Error), archive/unarchive as a 404
 * (NotFoundError) — callers must handle the RTK error path, not a boolean-false path. The only
 * branch that genuinely returns `false` with a 200 is unarchiving a session that is not archived.
 *
 * ## Cache scoping: patient-scoped session lists, not one global list
 * `getEvaluationSessionsByPatientId`'s tag is `EvaluationSession:PATIENT_<patientId>`, not a
 * blanket `LIST` — every session list view is inherently scoped to one patient (`patient_id` is
 * a required path param, per `getEvaluationSessionsParamsSchema`), so there's no legitimate
 * unscoped "all sessions" query to invalidate. `status` (active/archived) is just another query
 * arg RTK Query keys its cache on — archiving or unarchiving invalidates the *same*
 * `PATIENT_<patientId>` tag regardless of which `status` filter is currently mounted, so both the
 * active and archived list views refetch together, which is what makes an item move from one to
 * the other in the UI.
 *
 * Because `deleteEvaluationSession`/`archiveEvaluationSession`/`unarchiveEvaluationSession`/
 * `generateConclusion` only carry `evaluation_session_id` in their URL (no `patient_id`), and
 * their responses don't reliably carry it back either, their Arg here is `{ evaluationSessionId,
 * patientId }` — `patientId` is never sent over the wire (the descriptor's `path`/`body` never
 * reference it), it exists purely so the mutation can invalidate the correct patient-scoped list
 * tag without an extra round trip. Callers already have the patient in context (they're acting
 * from within that patient's evaluation list), so this costs nothing at the call site.
 * `generateConclusion` invalidates the list too: `EvaluationListItem.conclusion` is shown there.
 *
 * ## Network contract vs. client-side session state
 * Only real HTTP round trips are modeled here: create (start) -> resume (reload an
 * still-`initiated` session plus its question set) -> complete (submit the *entire* answer set in
 * one call) -> archive/unarchive/delete, plus the read-only details/list/generate-conclusion/
 * export-pdf actions. There is no incremental "save my answer to question N" or "save draft"
 * network endpoint anywhere in `evaluationSessionController` — `answerService.saveAnswers(...)`
 * is only ever invoked once, inside the `complete` handler, with the full answers array
 * (`completeEvaluationSessionSchema.answers` requires `.min(1)`). That means the in-progress
 * answer values a user is actively picking during a live session (mobile's `currentEvaluation` in
 * `apps/mobile/src/evaluation/stores/evaluation-store.ts`), current-question navigation position,
 * and any local draft/validation state are genuinely client-only — there is nothing for this file
 * to model for them, and no RTK Query cache entry should represent them. `resume` is real network
 * (it reloads the session shell + question list from the server so an interrupted session can be
 * picked back up), but the answers already typed before an interruption are not server-persisted
 * and are not part of what `resume` returns — whatever local persistence survives an app
 * restart (if any) is entirely the mobile app's own concern, not a network contract.
 */
export const evaluationSessionApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvaluationSessionsByPatientId: builder.query<
      EvaluationListItem[],
      { patientId: string; payload: GetEvaluationSessionsPayload }
    >({
      query: ({ patientId, payload }) => ({
        path: ['evaluation-sessions', 'patient', { param: 'patient_id', value: patientId }],
        method: 'get',
        query: payload,
      }),
      transformResponse: (response: EvaluationsResponse) => response.evaluations,
      providesTags: (result, _error, { patientId }) =>
        result
          ? [...result.map(({ id }) => ({ type: 'EvaluationSession' as const, id })), patientListTag(patientId)]
          : [patientListTag(patientId)],
    }),

    getEvaluationSessionDetails: builder.query<EvaluationDetailsResult, string>({
      query: (id) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: id }, 'details'],
        method: 'get',
      }),
      transformResponse: (response: SessionDetailsResponse) => response.sessionDetails,
      providesTags: (_result, _error, id) => [{ type: 'EvaluationSession', id }],
    }),

    getEvaluationSessionForResume: builder.query<EvaluationSessionWithQuestions, string>({
      query: (id) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: id }, 'resume'],
        method: 'get',
      }),
      transformResponse: (response: ResumeResponse) => response.evaluationSession,
      providesTags: (_result, _error, id) => [{ type: 'EvaluationSession', id }],
    }),

    createEvaluationSession: builder.mutation<EvaluationSessionWithQuestions, CreateEvaluationSessionPayload>({
      query: (body) => ({ path: ['evaluation-sessions'], method: 'post', body }),
      transformResponse: (response: CreateSessionResponse) => response.evaluationSession,
      invalidatesTags: (_result, _error, { patient_id }) => [
        patientListTag(patient_id),
        { type: 'Evaluation', id: 'MONTHLY_COUNT' },
      ],
    }),

    completeEvaluationSession: builder.mutation<
      EvaluationSession,
      { evaluationSessionId: string; patientId: string; payload: CompleteEvaluationSessionPayload }
    >({
      query: ({ evaluationSessionId, payload }) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: evaluationSessionId }, 'complete'],
        method: 'put',
        body: payload,
      }),
      transformResponse: (response: CompleteSessionResponse) => response.evaluationSession,
      invalidatesTags: (_result, _error, { evaluationSessionId, patientId }) => [
        { type: 'EvaluationSession', id: evaluationSessionId },
        patientListTag(patientId),
      ],
    }),

    deleteEvaluationSession: builder.mutation<{ deleted: boolean }, { evaluationSessionId: string; patientId: string }>(
      {
        query: ({ evaluationSessionId }) => ({
          path: ['evaluation-sessions', { param: 'evaluation_session_id', value: evaluationSessionId }],
          method: 'delete',
        }),
        transformResponse: (response: DeleteSessionResponse) => ({ deleted: response.deleted }),
        invalidatesTags: (_result, _error, { evaluationSessionId, patientId }) => [
          { type: 'EvaluationSession', id: evaluationSessionId },
          patientListTag(patientId),
          { type: 'Evaluation', id: 'MONTHLY_COUNT' },
        ],
      },
    ),

    archiveEvaluationSession: builder.mutation<
      { archived: boolean },
      { evaluationSessionId: string; patientId: string }
    >({
      query: ({ evaluationSessionId }) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: evaluationSessionId }, 'archive'],
        method: 'put',
      }),
      transformResponse: (response: ArchiveSessionResponse) => ({ archived: response.archived }),
      invalidatesTags: (_result, _error, { evaluationSessionId, patientId }) => [
        { type: 'EvaluationSession', id: evaluationSessionId },
        patientListTag(patientId),
      ],
    }),

    unarchiveEvaluationSession: builder.mutation<
      { unarchived: boolean },
      { evaluationSessionId: string; patientId: string }
    >({
      query: ({ evaluationSessionId }) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: evaluationSessionId }, 'unarchive'],
        method: 'put',
      }),
      transformResponse: (response: UnarchiveSessionResponse) => ({ unarchived: response.unarchived }),
      invalidatesTags: (_result, _error, { evaluationSessionId, patientId }) => [
        { type: 'EvaluationSession', id: evaluationSessionId },
        patientListTag(patientId),
      ],
    }),

    generateEvaluationConclusion: builder.mutation<
      GenerateConclusionEvaluationSessionResponse,
      { evaluationSessionId: string; patientId: string }
    >({
      query: ({ evaluationSessionId }) => ({
        path: [
          'evaluation-sessions',
          { param: 'evaluation_session_id', value: evaluationSessionId },
          'generate-conclusion',
        ],
        method: 'post',
      }),
      transformResponse: (response: GenerateConclusionResponse) => ({ conclusion: response.conclusion }),
      invalidatesTags: (_result, _error, { evaluationSessionId, patientId }) => [
        { type: 'EvaluationSession', id: evaluationSessionId },
        patientListTag(patientId),
      ],
    }),

    exportEvaluationSessionPdf: builder.mutation<ExportPdfEvaluationSessionResponse, string>({
      query: (evaluationSessionId) => ({
        path: ['evaluation-sessions', { param: 'evaluation_session_id', value: evaluationSessionId }, 'export-pdf'],
        method: 'post',
      }),
    }),
  }),
});

export const {
  useGetEvaluationSessionsByPatientIdQuery,
  useGetEvaluationSessionDetailsQuery,
  useGetEvaluationSessionForResumeQuery,
  useCreateEvaluationSessionMutation,
  useCompleteEvaluationSessionMutation,
  useDeleteEvaluationSessionMutation,
  useArchiveEvaluationSessionMutation,
  useUnarchiveEvaluationSessionMutation,
  useGenerateEvaluationConclusionMutation,
  useExportEvaluationSessionPdfMutation,
} = evaluationSessionApi;
